import React from "react";

import { useTinaPoll } from "@/pages/tina/hooks/use-tina-poll";
import { useRunsRepository } from "@/pages/tina/runs/hooks/use-runs-repository";
import {
  type EnrichmentRunFacts,
  collectEnrichmentRunFacts,
} from "@/pages/tina/runs/lib/enrichment-run-facts";

interface UseEnrichmentRunFactsOptions {
  /** False for a scrape run: it keeps the server-side summary and fetches nothing. */
  enabled: boolean;
  /** Poll while the run can still change, on the same cadence as the detail query. */
  isLive: boolean;
}

/**
 * The counts and the entity list for an enrichment run, read off its own rows.
 *
 * Fetches every item in the run — safe here and only here: enrichment runs have
 * a p95 of 2 items and a prod maximum of 500 (page size 200), against ~750 for
 * a cron scrape. See {@link collectEnrichmentRunFacts} for why the run summary
 * cannot answer this.
 *
 * Its own query key, deliberately: the show page's other full-item fetch
 * (`all-created-entities`) only runs once a scrape run is finalised, and
 * sharing a key would have made one of the two silently inherit the other's
 * gating.
 */
export function useEnrichmentRunFacts(
  runId: string,
  { enabled, isLive }: UseEnrichmentRunFactsOptions,
) {
  const repo = useRunsRepository();
  const query = useTinaPoll(
    ["tina", "runs", runId, "enrichment-facts"],
    () => repo.listAllItems(runId),
    { enabled: enabled && Boolean(runId), shouldPoll: () => isLive },
  );

  const facts: EnrichmentRunFacts | null = React.useMemo(
    () => (query.data ? collectEnrichmentRunFacts(query.data) : null),
    [query.data],
  );

  return { facts, isLoading: query.isLoading };
}
